import React from 'react';
import { ToastContainer } from 'react-toastify';
import assetIcon from '../../Images/asset-icon.png';
import SearchIcon from '../../Images/search-icon.svg';
import downloadIcon from '../../Images/download-icon.svg';
import Loader from '../../Components/common/loader/loader';
import type { SensorUomResponse } from '../../Services/serviceURL';
import {
  AssetDetails,
  AssetType,
  PlantData,
  UIParameter,
  compareAssetsByActiveStatus,
  getDownloadConfigTitle,
  getSaveAssetTitle,
  isActiveAssetStatus,
} from './createPlantDetailsModel';
import { CreatePlantDetailsParametersTable } from './CreatePlantDetailsParametersTable';

export interface CreatePlantDetailsViewProps {
  loading: boolean;
  plantData: PlantData | null;
  currentPlantData: unknown;
  stepOneClickable: boolean;
  hasPlantAssets: boolean;
  handleTabNavigation: (tabNumber: number) => void;
  handleAddAsset: () => void;
  assetSearchText: string;
  handleAssetSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  filteredAssets: AssetDetails[];
  selectedAssetFromHierarchy: AssetDetails | null;
  handleAssetClick: (asset: AssetDetails) => void;
  handleDownloadConfigFiles: () => void;
  saveDisabled: boolean;
  handleSave: () => void;
  assetTypes: AssetType[];
  selectedAsset: string;
  handleTypeChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  assetStatus: string;
  setAssetStatus: (value: string) => void;
  assetName: string;
  setAssetName: (value: string) => void;
  gatewayDeviceType: string;
  setGatewayDeviceType: (value: string) => void;
  gatewayDeviceId: string;
  handleGatewaySerialChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleGatewaySerialBlur: () => void;
  gatewaySerialTouched: boolean;
  gatewaySerialError: string;
  simNumber: string;
  handleSimNumberChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSimNumberBlur: () => void;
  simNumberTouched: boolean;
  simNumberError: string;
  controllerType: string;
  setControllerType: (value: string) => void;
  availableControllerTypes: string[];
  controllerDeviceId: string;
  setControllerDeviceId: (value: string) => void;
  parameters: UIParameter[];
  setParameters: React.Dispatch<React.SetStateAction<UIParameter[]>>;
  validUoms: SensorUomResponse[];
  handleBack: () => void;
  handleCancel: () => void;
  handleNext: () => void;
}

export const CreatePlantDetailsView: React.FC<CreatePlantDetailsViewProps> = (props) => {
  const {
    loading,
    stepOneClickable,
    hasPlantAssets,
    handleTabNavigation,
    handleAddAsset,
    assetSearchText,
    handleAssetSearchChange,
    filteredAssets,
    selectedAssetFromHierarchy,
    handleAssetClick,
    handleDownloadConfigFiles,
    saveDisabled,
    handleSave,
    assetTypes,
    selectedAsset,
    handleTypeChange,
    assetStatus,
    setAssetStatus,
    assetName,
    setAssetName,
    gatewayDeviceType,
    setGatewayDeviceType,
    gatewayDeviceId,
    handleGatewaySerialChange,
    handleGatewaySerialBlur,
    gatewaySerialTouched,
    gatewaySerialError,
    simNumber,
    handleSimNumberChange,
    handleSimNumberBlur,
    simNumberTouched,
    simNumberError,
    controllerType,
    setControllerType,
    availableControllerTypes,
    controllerDeviceId,
    setControllerDeviceId,
    parameters,
    setParameters,
    validUoms,
    handleBack,
    handleCancel,
    handleNext,
  } = props;

  const renderTabLabel = (stepNumber: string, label: string) => (
    <>
      <span className="tab-number">{stepNumber}</span>
      <span className="tab-label">{label}</span>
    </>
  );

  return (
    <div className="create-plant-details" style={{ position: 'relative' }}>
      {loading && <Loader isOverlay={true} />}
      <ToastContainer
        className="toastify-message"
        position="top-right"
        autoClose={3000}
        aria-label="Asset Save Notification"
        style={{
          marginTop: '150px',
          zIndex: 9999,
        }}
      />
      <div className="create-plant-tabs" role="tablist" aria-label="Create plant steps">
        {stepOneClickable ? (
          <button
            type="button"
            role="tab"
            className="tab completed clickable"
            onClick={() => handleTabNavigation(1)}
          >
            {renderTabLabel('1', 'Enter Plant Details')}
          </button>
        ) : (
          <div role="tab" aria-selected={false} className="tab completed">
            {renderTabLabel('1', 'Enter Plant Details')}
          </div>
        )}
        <div role="tab" aria-selected={true} className="tab active">
          {renderTabLabel('2', 'Build Asset Hierarchy')}
        </div>
        {hasPlantAssets ? (
          <button
            type="button"
            role="tab"
            className="tab clickable"
            onClick={() => handleTabNavigation(3)}
          >
            {renderTabLabel('3', 'Notification Setup')}
          </button>
        ) : (
          <div role="tab" aria-selected={false} className="tab disabled">
            {renderTabLabel('3', 'Notification Setup')}
          </div>
        )}
      </div>

      <div className="content-container">
        <div className="asset-hierarchy">
          <h3>Asset Hierarchy</h3>
          <button className="add-asset-btn" type="button" onClick={handleAddAsset}>
            Add New Asset
          </button>
          <div className="search-box">
            <label htmlFor="asset-hierarchy-search" className="visually-hidden">
              Search by asset name
            </label>
            <img className="search-icon-plant" src={SearchIcon} alt="" />
            <input
              id="asset-hierarchy-search"
              type="search"
              placeholder="Search By Asset Name"
              value={assetSearchText}
              onChange={handleAssetSearchChange}
            />
          </div>

          <div className="asset-hierarchy assets-hierarchy">
            {(Array.isArray(filteredAssets) ? filteredAssets : [])
              .slice()
              .sort(compareAssetsByActiveStatus)
              .map((asset) => {
                const isSelected = selectedAssetFromHierarchy?.id === asset.id;
                const isActive = isActiveAssetStatus(asset.status);

                return (
                  <button
                    key={asset.id}
                    type="button"
                    className={`asset-item ${isSelected ? 'selected' : ''} ${isActive ? 'active' : 'inactive'}`}
                    onClick={() => handleAssetClick(asset)}
                    title={`${asset.type} - ${asset.name}`}
                  >
                    <img className="asset-icon" src={assetIcon} alt="" />
                    <span className="asset-item-label">{`${asset.type} - ${asset.name}`}</span>
                  </button>
                );
              })}
          </div>
        </div>
        <div className="asset-details">
          <div className="asset-details-header">
            {selectedAssetFromHierarchy ? (
              <h3>{`${selectedAssetFromHierarchy.type} - ${selectedAssetFromHierarchy.name}`}</h3>
            ) : (
              <h3>New Asset Configuration Details</h3>
            )}
            <div className="header-buttons">
              <button
                type="button"
                className="download-config-btn-plant"
                onClick={handleDownloadConfigFiles}
                disabled={!selectedAssetFromHierarchy || loading}
                title={getDownloadConfigTitle(Boolean(selectedAssetFromHierarchy))}
              >
                <img src={downloadIcon} alt="" className="download-icon" />
                <span className="download-config-label">Download Config Files</span>
              </button>
              <button
                type="button"
                className={`save-btn-plant ${saveDisabled ? 'disabled' : ''}`}
                onClick={handleSave}
                disabled={saveDisabled}
                title={getSaveAssetTitle(saveDisabled)}
              >
                Save
              </button>
            </div>
          </div>
          <div className="form-group">
            <div className="form-grid-three">
              <div className="form-group-plant">
                <label htmlFor="asset-type-select">Asset Type</label>
                <select
                  id="asset-type-select"
                  value={selectedAsset}
                  onChange={handleTypeChange}
                  disabled={Boolean(selectedAssetFromHierarchy)}
                  title={
                    selectedAssetFromHierarchy
                      ? 'Cannot change asset type for existing assets'
                      : 'Select asset type'
                  }
                  className={selectedAssetFromHierarchy ? 'disabled-select' : ''}
                >
                  {Array.isArray(assetTypes) &&
                    assetTypes.length > 0 &&
                    assetTypes.map((type) => (
                      <option key={type.typeName} value={type.typeName}>
                        {type.typeName}
                      </option>
                    ))}
                </select>
                {selectedAssetFromHierarchy && (
                  <div className="field-disabled-note">Asset Type Cannot be changed for existing assets</div>
                )}
              </div>
              <div className="form-group-plant">
                <label htmlFor="asset-status-select">Asset Status</label>
                <select
                  id="asset-status-select"
                  value={assetStatus}
                  onChange={(event) => setAssetStatus(event.target.value)}
                >
                  <option value="Enabled">Active</option>
                  <option value="Disabled">Inactive</option>
                </select>
              </div>
              <div className="form-group-plant">
                <label htmlFor="asset-name-input">
                  Asset Name<span className="required">*</span>
                </label>
                <input
                  id="asset-name-input"
                  type="text"
                  value={assetName}
                  onChange={(event) => setAssetName(event.target.value)}
                  placeholder="Enter asset name"
                />
              </div>
            </div>
            <div className="form-grid-three">
              <div className="form-group-plant">
                <label htmlFor="gateway-device-type-select">Gateway / IoT Edge Device Type</label>
                <select
                  id="gateway-device-type-select"
                  value={gatewayDeviceType}
                  onChange={(event) => setGatewayDeviceType(event.target.value)}
                >
                  <option value="">Select Device Type</option>
                  <option value="SLE">SLE</option>
                </select>
              </div>

              <div className="form-group-plant">
                <label htmlFor="gateway-serial-input">
                  Gateway Serial Number<span className="required">*</span>
                </label>
                <input
                  id="gateway-serial-input"
                  type="text"
                  value={gatewayDeviceId}
                  onChange={handleGatewaySerialChange}
                  onBlur={handleGatewaySerialBlur}
                  className={gatewaySerialTouched && gatewaySerialError ? 'input-error' : undefined}
                />
                {gatewaySerialTouched && gatewaySerialError && (
                  <span className="form-error-text">{gatewaySerialError}</span>
                )}
              </div>

              <div className="form-group-plant">
                <label htmlFor="sim-number-input">
                  SIM Number<span className="required">*</span>
                </label>
                <input
                  id="sim-number-input"
                  type="text"
                  value={simNumber}
                  onChange={handleSimNumberChange}
                  onBlur={handleSimNumberBlur}
                  className={simNumberTouched && simNumberError ? 'input-error' : ''}
                  placeholder="Enter SIM number (digits only)"
                  onKeyDown={(event) => {
                    if (event.key === ' ') {
                      event.preventDefault();
                    }
                  }}
                />
                {simNumberTouched && simNumberError && (
                  <span className="form-error-text">{simNumberError}</span>
                )}
              </div>
            </div>
            <div className="form-grid-twos">
              <div className="form-group-plant-controller">
                <label htmlFor="controller-type-select">Controller Type</label>
                <select
                  id="controller-type-select"
                  value={controllerType}
                  onChange={(event) => setControllerType(event.target.value)}
                >
                  <option value="">Select Controller Type</option>
                  {availableControllerTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group-plant-number">
                <label htmlFor="controller-serial-input">
                  Controller Serial Number <span className="required">*</span>
                </label>
                <input
                  id="controller-serial-input"
                  type="text"
                  value={controllerDeviceId}
                  onChange={(event) => setControllerDeviceId(event.target.value)}
                />
              </div>
            </div>
            <div className="operational-parameters">
              <h4>Operational Parameters / Attributes Settings</h4>
              <CreatePlantDetailsParametersTable
                parameters={parameters}
                setParameters={setParameters}
                validUoms={validUoms}
                saveDisabled={saveDisabled}
                onSave={handleSave}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="button-container">
        <button type="button" className="cancel-btn backs-button" onClick={handleBack}>
          Previous
        </button>
        <button type="button" className="cancel-btn" onClick={handleCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="cancel-btn"
          onClick={hasPlantAssets ? handleNext : undefined}
          disabled={!hasPlantAssets}
        >
          Next
        </button>
      </div>
    </div>
  );
};
